import { CloudArrowUpIcon, SpinnerGapIcon, XIcon } from '@phosphor-icons/react';
import { useEffect, useRef, useState, type ChangeEvent, type DragEvent } from 'react';

import { useToast } from '@/components/feedback/toast';

import styles from './image-attach.module.scss';

/** A picked image and its (simulated) upload state. */
export type AttachedImage = {
  id: string;
  name: string;
  previewUrl: string;
  status: 'uploading' | 'success';
};

export type ImageAttachProps = {
  value: AttachedImage[];
  onChange: (next: AttachedImage[]) => void;
  /** Max images before the dropzone disables (default 5). */
  maxCount?: number;
  /** Per-file size cap in MB (default 5). */
  maxSizeMb?: number;
  /** Comma-separated accepted MIME types (default JPEG/PNG). */
  accept?: string;
  /** Override the default dropzone helper copy. */
  helpText?: string;
  /** Render the dropzone in an error (red dashed) state — form-level validation. */
  invalid?: boolean;
};

const DEFAULT_ACCEPT = 'image/jpeg,image/png';

/**
 * Drag-and-drop image attach area (Figma support/warranty modals): a dashed dropzone plus
 * a strip of square thumbnails with remove badges. Oversize / wrong-type files surface the
 * dark Figma toast and are rejected; a brief simulated upload exercises any "Submit
 * disabled until uploads finish" rule the consumer enforces.
 */
export function ImageAttach({
  value,
  onChange,
  maxCount = 5,
  maxSizeMb = 5,
  accept = DEFAULT_ACCEPT,
  helpText,
  invalid = false,
}: ImageAttachProps) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const seq = useRef(0);
  const [dragOver, setDragOver] = useState(false);

  const acceptedTypes = accept.split(',');
  const maxBytes = maxSizeMb * 1024 * 1024;

  // Mirror the latest value so the simulated-upload timeout reads fresh state, and so the
  // unmount cleanup can revoke every object URL without a stale closure. (Synced in an
  // effect — React 19 forbids ref writes during render.)
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  });
  useEffect(() => () => valueRef.current.forEach((img) => URL.revokeObjectURL(img.previewUrl)), []);

  const atMax = value.length >= maxCount;

  const settle = (id: string) => {
    window.setTimeout(() => {
      onChange(
        valueRef.current.map((img) => (img.id === id ? { ...img, status: 'success' } : img)),
      );
    }, 700);
  };

  const addFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const existing = valueRef.current;
    const added: AttachedImage[] = [];

    for (const file of Array.from(files)) {
      if (existing.length + added.length >= maxCount) {
        toast.error({ title: `Maximum of ${maxCount} images reached` });
        break;
      }
      if (!acceptedTypes.includes(file.type)) {
        toast.error({ title: 'Only JPG or PNG images are allowed' });
        continue;
      }
      if (file.size > maxBytes) {
        toast.error({ title: `File size exceeds ${maxSizeMb}mb. Try another file` });
        continue;
      }
      seq.current += 1;
      added.push({
        id: `img-${seq.current}`,
        name: file.name,
        previewUrl: URL.createObjectURL(file),
        status: 'uploading',
      });
    }

    if (added.length === 0) return;
    onChange([...existing, ...added]);
    added.forEach((img) => settle(img.id));
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (!atMax) addFiles(e.dataTransfer.files);
  };

  const remove = (id: string) => {
    const target = value.find((img) => img.id === id);
    if (target) URL.revokeObjectURL(target.previewUrl);
    onChange(value.filter((img) => img.id !== id));
  };

  const open = () => {
    if (!atMax) inputRef.current?.click();
  };

  const defaultHelp = `Add up to ${maxCount} images (JPEG, JPG or PNG) max ${maxSizeMb}MB each.`;

  return (
    <div className={styles.root}>
      <div
        className={[
          styles.dropzone,
          dragOver ? styles.dragOver : undefined,
          atMax ? styles.disabled : undefined,
          invalid ? styles.invalid : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
        role={atMax ? undefined : 'button'}
        tabIndex={atMax ? undefined : 0}
        aria-disabled={atMax || undefined}
        onClick={open}
        onKeyDown={(e) => {
          if (!atMax && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            open();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!atMax) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <CloudArrowUpIcon size={28} className={styles.cloud} aria-hidden />
        <p className={styles.help}>
          {atMax ? `Maximum of ${maxCount} images reached` : (helpText ?? defaultHelp)}
        </p>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          className={styles.input}
          onChange={onInputChange}
          tabIndex={-1}
        />
      </div>

      {value.length > 0 ? (
        <ul className={styles.thumbs}>
          {value.map((img) => (
            <li key={img.id} className={styles.thumb}>
              <img src={img.previewUrl} alt={img.name} className={styles.thumbImg} />
              {img.status === 'uploading' ? (
                <span className={styles.uploading} aria-label="Uploading">
                  <SpinnerGapIcon size={18} className={styles.spinner} />
                </span>
              ) : null}
              <button
                type="button"
                className={styles.remove}
                aria-label={`Remove ${img.name}`}
                onClick={() => remove(img.id)}
              >
                <XIcon size={11} weight="bold" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
